import { routeConstant } from "shared/routes/routeConstant";
import { SUPPORTED_FORMATS } from "./enum";
import { store } from "shared/redux/store";
import { resetAuthReducer } from "shared/redux/reducers/authSlice";
import { toastMessage } from "shared/components/common/toast";
import { Theme, RoleTheme } from "shared/types/chatBoard";
import moment from "moment";

interface Dimensions {
  width: number;
  height: number;
}

function getWindowDimensions(): Dimensions {
  if (typeof window !== "undefined") {
    const { innerWidth: width, innerHeight: height } = window;
    return { width, height };
  }
  return { width: 0, height: 0 };
}

function isImage(filetype: string): boolean {
  return SUPPORTED_FORMATS.includes(filetype);
}

const ERROR_TOAST_DEBOUNCE_MS = 2000;
let lastErrorToastAt = 0;
let lastErrorToastMessage = "";

export function showErrorToastOnce(message: string) {
  const now = Date.now();
  const isDuplicate =
    message === lastErrorToastMessage &&
    now - lastErrorToastAt < ERROR_TOAST_DEBOUNCE_MS;
  if (isDuplicate) return;
  lastErrorToastAt = now;
  lastErrorToastMessage = message;
  toastMessage("error", message);
}

function handleErrors(err: any) {
  if (err?.__sessionExpiredHandled) return;

  if (!err?.response) {
    if (err?.code === "ERR_NETWORK" || err?.message?.includes("Network Error")) {
      showErrorToastOnce("Network error. Please check your internet connection or try again later.");
      return;
    }
    if (err?.code === "ECONNABORTED" || err?.message?.includes("timeout")) {
      showErrorToastOnce("Request timeout. Please try again.");
      return;
    }
    if (err?.message?.includes("CORS") || err?.message?.includes("cors")) {
      showErrorToastOnce("CORS error. Please contact support.");
      return;
    }
    showErrorToastOnce(err?.message || "Network error. Please try again.");
    return;
  }

  const statusText = err?.response?.statusText;
  const statusCode = err?.response?.status;
  const data = err?.response?.data;

  if (statusCode === 0 || (statusCode && statusCode >= 500 && statusCode < 600)) {
    showErrorToastOnce("Server error. Please try again later.");
    return;
  }

  if (typeof data?.message === "object" && Array.isArray(data?.message)) {
    const firstMessage = data.message[0];
    showErrorToastOnce(firstMessage);
    return;
  }

  if (typeof data?.message === "string" && data.message.trim() !== "") {
    showErrorToastOnce(data.message);
    return;
  }

  if (typeof data?.error === "string" && data.error.trim() !== "") {
    showErrorToastOnce(data.error);
    return;
  }

  if (typeof data === "string" && data.trim() !== "") {
    const fallback = statusText || err?.message || "Something went wrong";
    const prefix = statusCode ? `${statusCode} - ` : "";
    showErrorToastOnce(`${prefix}${fallback}`);
    return;
  }

  if (typeof err?.message === "string" && err.message.trim() !== "") {
    showErrorToastOnce(err.message);
    return;
  }

  showErrorToastOnce("Something went wrong");
}

function findScreenTitle(pathname: string): string {
  const matchesRoute = (routePath: string, actualPath: string): boolean => {
    if (routePath === actualPath) return true;
    const routePattern = routePath.replace(/:[^/]+/g, "[^/]+");
    const regex = new RegExp(`^${routePattern}$`);
    return regex.test(actualPath);
  };

  const findRoute = (obj: any): any => {
    if (obj?.path && matchesRoute(obj.path, pathname)) {
      return obj;
    }
    if (typeof obj === "object" && obj !== null) {
      for (const key in obj) {
        const result = findRoute(obj[key]);
        if (result) return result;
      }
    }
    return null;
  };

  const route = findRoute(routeConstant);
  return route?.title || "";
}

const resetRedux = () => {
  const auth = store.getState()?.root?.auth;
  if (auth?.token) {
    store.dispatch(resetAuthReducer());
    showErrorToastOnce("Your session has expired. Please login again.");
    window.location.reload();
  }
};

function getTopPosition(divElement: HTMLElement): number {
  return (
    divElement.getBoundingClientRect().top - document.documentElement.clientTop
  );
}

function getLeftPosition(divElement: HTMLElement): number {
  return (
    divElement.getBoundingClientRect().left -
    document.documentElement.clientLeft
  );
}

function getDistanceBetweenDivs(div1: HTMLElement, div2: HTMLElement): number {
  const rect1 = div1.getBoundingClientRect();
  const rect2 = div2.getBoundingClientRect();
  const deltaX = Math.max(0, rect1.x - rect2.x) - (rect1.width + rect2.width);
  const deltaY = Math.max(0, rect1.y - rect2.y) - (rect1.height + rect2.height);

  return Math.sqrt(deltaX ** 2 + deltaY ** 2);
}

function getPercentageFromString(num: string): number {
  const parsedNum = parseFloat(num.replace("%", ""));
  return isNaN(parsedNum) ? 1 : parsedNum / 100 + 1;
}

function getNestedValue<T>(obj: Record<string, any>, key: string): T | null {
  return key.split(".").reduce((o, i) => (o ? o[i] : null), obj) as T | null;
}

const themes: Theme[] = ["theme1", "theme2", "theme3", "theme4"];

function getSequentialTheme(index: number): Theme {
  return themes[index % themes.length];
}

function getRoleTheme(role: string): RoleTheme {
  const roleLower = role.toLowerCase();

  switch (roleLower) {
    case "designer":
      return {
        bg: "#FDE5DD",
        textColor: "#E0734F",
      };
    case "client":
      return {
        bg: "#E5F3FF",
        textColor: "#0085FF",
      };
    case "collaborator":
      return {
        bg: "#E9DDFD",
        textColor: "#7D4CCF",
      };
    case "builder":
      return {
        bg: "#E7F7ED",
        textColor: "#088B3A",
      };
    default:
      return {
        bg: "#ed1c240f",
        textColor: "#ED1C24",
      };
  }
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
}

function formatDateTime24h(dateString: string | null | undefined): string {
  if (!dateString) return "";
  const date = moment(dateString);
  if (!date.isValid()) return "";
  return date.format("MM/DD/YYYY HH:mm");
}

const PROJECT_STATUS_LABELS: Record<string, string> = {
  onboarding: "Onboarding",
  consultation: "Consultation",
  preliminary: "Preliminary",
  preliminary_finals: "Preliminary Finals",
  final_review: "Final Review",
  completed: "Completed",
};

function getProjectStatusLabel(status: string | null | undefined): string {
  if (!status) return "";
  const key = status.toLowerCase();
  return PROJECT_STATUS_LABELS[key] || status;
}

function formatTimestamp(dateString: string): string {
  if (!dateString) return "";
  const date = moment(dateString);
  const now = moment();
  const diffInHours = now.diff(date, "hours");
  const diffInDays = now.diff(date, "days");

  if (diffInHours < 1) {
    const diffInMinutes = now.diff(date, "minutes");
    const minutesFormatted = diffInMinutes.toString().padStart(2, "0");
    return diffInMinutes <= 1 ? "just now" : `${minutesFormatted} minutes ago`;
  } else if (diffInHours < 24) {
    const hoursFormatted = diffInHours.toString().padStart(2, "0");
    return `${hoursFormatted} ${diffInHours === 1 ? "hour" : "hours"} ago`;
  } else if (diffInDays < 7) {
    const daysFormatted = diffInDays.toString().padStart(2, "0");
    return `${daysFormatted} ${diffInDays === 1 ? "day" : "days"} ago`;
  } else {
    return date.format("MM/DD/YYYY HH:mm");
  }
}

export interface GroupedNotificationItem {
  keyword: string;
  dateFormatted: string;
  year: string;
  notifications: any[];
}

function groupNotificationsByDate<T extends { created_at: string }>(
  notificationsList: T[]
): GroupedNotificationItem[] {
  const grouped: { [key: string]: T[] } = {};
  const today = moment().startOf("day");
  const yesterday = moment().subtract(1, "days").startOf("day");

  notificationsList.forEach((notification) => {
    const notificationDate = moment(notification.created_at).startOf("day");
    let dateKey: string;

    if (notificationDate.isSame(today, "day")) {
      dateKey = "today";
    } else if (notificationDate.isSame(yesterday, "day")) {
      dateKey = "yesterday";
    } else {
      dateKey = notificationDate.format("YYYY-MM-DD");
    }

    if (!grouped[dateKey]) {
      grouped[dateKey] = [];
    }
    grouped[dateKey].push(notification);
  });

  return Object.keys(grouped)
    .map((key) => {
      const notifications = grouped[key];
      const firstNotification = notifications[0];
      const notificationDate = moment(firstNotification.created_at).startOf("day");
      const today = moment().startOf("day");
      const yesterday = moment().subtract(1, "days").startOf("day");

      let keyword: string;
      let dateFormatted: string;
      let year: string;

      if (notificationDate.isSame(today, "day")) {
        keyword = "Today";
        dateFormatted = moment(firstNotification.created_at).format("MMM D");
        year = moment(firstNotification.created_at).format("YYYY");
      } else if (notificationDate.isSame(yesterday, "day")) {
        keyword = "Yesterday";
        dateFormatted = moment(firstNotification.created_at).format("MMM D");
        year = moment(firstNotification.created_at).format("YYYY");
      } else {
        keyword = moment(firstNotification.created_at).format("dddd");
        dateFormatted = moment(firstNotification.created_at).format("MMM D");
        year = moment(firstNotification.created_at).format("YYYY");
      }

      return {
        keyword,
        dateFormatted,
        year,
        notifications,
      };
    })
    .sort((a, b) => {
      const aDate = moment(a.notifications[0].created_at).startOf("day");
      const bDate = moment(b.notifications[0].created_at).startOf("day");
      const today = moment().startOf("day");
      const yesterday = moment().subtract(1, "days").startOf("day");

      if (aDate.isSame(today, "day")) return -1;
      if (bDate.isSame(today, "day")) return 1;
      if (aDate.isSame(yesterday, "day")) return -1;
      if (bDate.isSame(yesterday, "day")) return 1;
      return bDate.diff(aDate);
    });
}

export const downloadBlobAsFile = (
  blobData: Blob | any,
  fileName: string,
  mimeType: string = "application/zip"
) => {
  const blob =
    blobData instanceof Blob
      ? blobData
      : new Blob([blobData], { type: mimeType });

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Designer-upload S3 keys are stored as `<original-base>-<timestamp>-<uuid>.<ext>`
// so every object is unique. For display we strip that generated suffix and show
// the original filename the user uploaded (e.g. "proxy-image.jpg"). Paths without
// the suffix (older uploads) are returned unchanged.
const UPLOAD_KEY_SUFFIX =
  /-\d{13}-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const getDisplayFileName = (pathOrName: string): string => {
  if (!pathOrName) return "File";
  const segment = (pathOrName.split("/").pop() || "File").split("?")[0];
  const dot = segment.lastIndexOf(".");
  const base = dot > 0 ? segment.slice(0, dot) : segment;
  const ext = dot > 0 ? segment.slice(dot) : "";
  const original = base.replace(UPLOAD_KEY_SUFFIX, "");
  return `${original || base}${ext}`;
};

export {
  getWindowDimensions,
  isImage,
  resetRedux,
  getTopPosition,
  getLeftPosition,
  getDistanceBetweenDivs,
  getPercentageFromString,
  getNestedValue,
  findScreenTitle,
  getSequentialTheme,
  getRoleTheme,
  handleErrors,
  formatFileSize,
  formatTimestamp,
  formatDateTime24h,
  getProjectStatusLabel,
  groupNotificationsByDate,
};
